import { IonAvatar } from "@ionic/react";
import { format } from "@formkit/tempo";
import EmptyAvatar from "@/assets/empty_avatar.svg";
import { BoomerIcon } from "@/lib/icons/boomer.icon";
import { WhatsappIcon } from "@/lib/icons/whatsapp.icon";
import type { TypedMessage } from "@/lib/query/conversation/conversation.types";
import { cx } from "@/lib/style/cva.config";
import { TemplateMessage } from "./template-message";

interface MessageBubbleProps {
  message: TypedMessage;
  participantMap: Map<
    string,
    {
      side?: "ORGANIZATION" | "CONTACT" | null;
      userId?: string;
      avatarUrl?: string;
      displayName?: string;
    }
  >;
}

export function MessageBubble({ message, participantMap }: MessageBubbleProps) {
  const senderInfo = participantMap.get(message.senderId);
  // Message is outbound if sender is on the ORGANIZATION side (manager/admin)
  // This means ALL messages from organization members appear on the right,
  // even if sent by a different manager in the same conversation
  // Fallback to message.direction for backwards compatibility
  const isOutbound = senderInfo?.side
    ? senderInfo.side === "ORGANIZATION"
    : message.direction === "OUTBOUND";

  const timestamp = format(new Date(message.createdAt), "HH:mm");

  // Channel icon for the message
  const ChannelIcon =
    message.channel === "BOOMER" ? (
      <BoomerIcon width={12} height={12} className="shrink-0 text-brand-500" />
    ) : message.channel === "WHATSAPP" ? (
      <WhatsappIcon width={12} height={12} className="shrink-0" />
    ) : null;

  return (
    <div className={cx("flex w-full", isOutbound ? "justify-end pl-[88px]" : "items-end gap-2.5")}>
      {/* Avatar for incoming messages */}
      {!isOutbound && (
        <IonAvatar className="size-8 shrink-0">
          <img
            src={senderInfo?.avatarUrl || EmptyAvatar}
            alt={senderInfo?.displayName || "Sender"}
            className="size-full rounded-full object-cover"
          />
        </IonAvatar>
      )}

      <div className={cx("flex flex-1 flex-col gap-1.5", isOutbound ? "items-end" : "items-start")}>
        {/* Timestamp and channel indicator */}
        <div
          className={cx(
            "flex items-center gap-1 px-2 text-[13px] font-medium leading-[1.5] text-txt-primary",
            isOutbound ? "flex-row" : "flex-row-reverse",
          )}
        >
          <span>{timestamp}</span>
          {ChannelIcon}
        </div>

        {/* Message bubble - use TemplateMessage for messages with template components */}
        {message.templateComponents?.buttons?.length ? (
          <TemplateMessage
            body={message.body}
            templateComponents={message.templateComponents}
            isOutbound={isOutbound}
          />
        ) : (
          message.body && (
            <div
              className={cx(
                "p-4",
                isOutbound
                  ? "rounded-bl-xl rounded-tl-xl rounded-tr-xl border border-bordercolor-primary bg-brand-100"
                  : "rounded-br-xl rounded-tl-xl rounded-tr-xl border-[0.5px] border-bordercolor-primary bg-surface-primary",
              )}
            >
              <p className="whitespace-pre-wrap text-[15px] font-medium leading-[1.6] text-txt-primary">
                {message.body}
              </p>
            </div>
          )
        )}

        {/* Media attachments */}
        {message.media && message.media.length > 0 && (
          <div className="mt-1 flex flex-col gap-1">
            {message.media.map((item, index) => (
              <MediaPreview key={index} media={item} isOutbound={isOutbound} />
            ))}
          </div>
        )}

        {/* Status indicator for failed messages */}
        {isOutbound && message.status === "FAILED" && (
          <span className="mt-1 text-xs text-red-500">!</span>
        )}
      </div>
    </div>
  );
}

interface MediaPreviewProps {
  media: {
    mediaUrl: string;
    fileName: string;
    contentType: string;
  };
  isOutbound: boolean;
}

function MediaPreview({ media, isOutbound }: MediaPreviewProps) {
  const isImage = media.contentType.startsWith("image/");

  if (isImage) {
    return (
      <img
        src={media.mediaUrl}
        alt={media.fileName}
        className={cx(
          "max-h-48 max-w-full rounded-lg object-contain",
          isOutbound ? "rounded-br-md" : "rounded-bl-md",
        )}
      />
    );
  }

  // For other file types, show a file card
  return (
    <a
      href={media.mediaUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cx(
        "flex items-center gap-2 rounded-lg px-3 py-2 text-sm",
        isOutbound ? "bg-brand-400 text-white" : "bg-gray-200 text-txt-primary",
      )}
    >
      <span className="truncate">{media.fileName}</span>
    </a>
  );
}
